import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fmtDop } from "../helpers";

/**
 * ProductRelationsManager — connect a product with other products
 * (related, alternative, bundle, frequently_bought, upsell, cross_sell).
 *
 * Interactive search-autocomplete to find products by name/sku, with
 * inline category chips, drag-free reordering via ↑/↓ buttons, and
 * per-relation notes.
 */

const RELATION_TYPES = [
  { id: "related",           es: "Relacionado",        en: "Related",            icon: "🔗" },
  { id: "alternative",       es: "Alternativa",        en: "Alternative",        icon: "🔄" },
  { id: "bundle",            es: "Bundle",             en: "Bundle",             icon: "📦" },
  { id: "frequently_bought", es: "Comprado junto",     en: "Frequently bought",  icon: "🛒" },
  { id: "upsell",            es: "Upsell",             en: "Upsell",             icon: "⬆️" },
  { id: "cross_sell",        es: "Cross-sell",         en: "Cross-sell",         icon: "↔️" },
];

export function ProductRelationsManager({ productId, lang, toast }) {
  const [relations, setRelations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("related");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("product_relations")
      .select(`
        id, relation_type, position, note, related_product_id,
        related:products!product_relations_related_product_id_fkey ( id, name, image_url, price, sku, is_active )
      `)
      .eq("product_id", productId)
      .order("relation_type")
      .order("position");
    if (error) toast(`❌ ${error.message}`);
    setRelations(data || []);
    setLoading(false);
  }, [productId, toast]);

  useEffect(() => { load(); }, [load]);

  // Debounced search
  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const q = search.trim();
      const { data } = await supabase
        .from("products")
        .select("id, name, image_url, price, sku, is_active")
        .neq("id", productId)
        .or(`name.ilike.%${q}%,sku.ilike.%${q}%`)
        .eq("is_active", true)
        .limit(8);
      setResults(data || []);
      setSearching(false);
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, productId]);

  const byType = useMemo(() => {
    const m = {};
    for (const t of RELATION_TYPES) m[t.id] = [];
    for (const r of relations) (m[r.relation_type] ||= []).push(r);
    return m;
  }, [relations]);

  const linkedIds = useMemo(
    () => new Set(relations.filter((r) => r.relation_type === activeType).map((r) => r.related_product_id)),
    [relations, activeType],
  );

  const link = async (p) => {
    if (linkedIds.has(p.id)) { toast(`✅ ${lang === "es" ? "Ya conectado" : "Already linked"}`); return; }
    const pos = (byType[activeType] || []).length;
    const { data, error } = await supabase.from("product_relations").insert({
      product_id: productId, related_product_id: p.id, relation_type: activeType, position: pos,
    }).select(`
      id, relation_type, position, note, related_product_id,
      related:products!product_relations_related_product_id_fkey ( id, name, image_url, price, sku, is_active )
    `).single();
    if (error) { toast(`❌ ${error.message}`); return; }
    setRelations((p) => [...p, data]);
    setSearch(""); setResults([]); setOpen(false);
    toast(`✅ ${lang === "es" ? "Conectado" : "Linked"}`);
  };

  const unlink = async (r) => {
    const { error } = await supabase.from("product_relations").delete().eq("id", r.id);
    if (error) { toast(`❌ ${error.message}`); return; }
    setRelations((p) => p.filter((x) => x.id !== r.id));
  };

  const move = async (r, dir) => {
    const list = (byType[r.relation_type] || []).slice().sort((a, b) => a.position - b.position);
    const idx = list.findIndex((x) => x.id === r.id);
    const swap = list[idx + dir];
    if (!swap) return;
    const a = { id: r.id, position: swap.position };
    const b = { id: swap.id, position: r.position };
    setRelations((prev) => prev.map((x) => x.id === a.id ? { ...x, position: a.position } : x.id === b.id ? { ...x, position: b.position } : x));
    await Promise.all([
      supabase.from("product_relations").update({ position: a.position }).eq("id", a.id),
      supabase.from("product_relations").update({ position: b.position }).eq("id", b.id),
    ]);
  };

  const setNote = async (r, note) => {
    setRelations((p) => p.map((x) => x.id === r.id ? { ...x, note } : x));
    const { error } = await supabase.from("product_relations").update({ note: note || null }).eq("id", r.id);
    if (error) toast(`❌ ${error.message}`);
  };

  if (!productId) {
    return (
      <div style={{ fontSize: 11, color: "var(--tx3)", padding: 10 }}>
        {lang === "es" ? "Guarda el producto primero para conectar otros productos." : "Save the product first."}
      </div>
    );
  }

  const list = (byType[activeType] || []).slice().sort((a, b) => a.position - b.position);

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
        {RELATION_TYPES.map((t) => {
          const count = (byType[t.id] || []).length;
          const active = t.id === activeType;
          return (
            <button key={t.id} type="button" onClick={() => setActiveType(t.id)}
              style={{
                fontSize: 11, padding: "4px 10px", borderRadius: 14, cursor: "pointer",
                border: `1px solid ${active ? "var(--ac, #3b82f6)" : "var(--bd2)"}`,
                background: active ? "var(--ac, #3b82f6)" : "var(--bg2)",
                color: active ? "#fff" : "var(--tx2)",
              }}>
              {t.icon} {lang === "es" ? t.es : t.en}
              {count > 0 && <span style={{ marginLeft: 4, fontWeight: 600 }}>· {count}</span>}
            </button>
          );
        })}
      </div>

      {/* Search / connect */}
      <div style={{ position: "relative", marginBottom: 10 }}>
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={lang === "es" ? `🔍 Buscar producto para conectar como ${RELATION_TYPES.find((x) => x.id === activeType)?.es?.toLowerCase()}…` : "🔍 Search a product to link…"}
          style={{ ...inputBox, width: "100%" }} />
        {open && (search.trim() || searching) && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
            background: "var(--bg)", border: "1px solid var(--bd2)", borderRadius: 6,
            marginTop: 4, maxHeight: 280, overflow: "auto", boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          }}>
            {searching && <div style={{ padding: 10, fontSize: 11, color: "var(--tx3)" }}>{lang === "es" ? "Buscando…" : "Searching…"}</div>}
            {!searching && results.length === 0 && (
              <div style={{ padding: 10, fontSize: 11, color: "var(--tx3)" }}>
                {lang === "es" ? "Sin resultados" : "No results"}
              </div>
            )}
            {results.map((p) => {
              const linked = linkedIds.has(p.id);
              return (
                <button key={p.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => link(p)}
                  disabled={linked}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: 8, border: 0, borderBottom: "1px solid var(--bd2)",
                    background: linked ? "var(--bg2)" : "transparent",
                    color: "var(--tx)", cursor: linked ? "default" : "pointer", textAlign: "left",
                  }}>
                  {p.image_url
                    ? <img src={p.image_url} alt="" style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4 }} />
                    : <div style={{ width: 32, height: 32, borderRadius: 4, background: "var(--bg2)", display: "flex", alignItems: "center", justifyContent: "center" }}>📦</div>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: "var(--tx3)" }}>{p.sku || "—"} · {fmtDop(p.price)}</div>
                  </div>
                  <span style={{ fontSize: 11, color: linked ? "var(--tx3)" : "var(--ac, #3b82f6)", fontWeight: 600 }}>
                    {linked ? "✓" : `+ ${lang === "es" ? "Conectar" : "Link"}`}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ fontSize: 11, color: "var(--tx3)" }}>{lang === "es" ? "Cargando…" : "Loading…"}</div>
      ) : list.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--tx3)", padding: 16, border: "1px dashed var(--bd2)", borderRadius: 6, textAlign: "center" }}>
          {lang === "es"
            ? `Sin productos conectados como "${RELATION_TYPES.find((x) => x.id === activeType)?.es}". Busca arriba para añadir.`
            : `No products linked as "${RELATION_TYPES.find((x) => x.id === activeType)?.en}". Search above to add.`}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {list.map((r, i) => (
            <div key={r.id} style={{
              display: "flex", alignItems: "center", gap: 8, padding: 8,
              border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)",
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <button type="button" onClick={() => move(r, -1)} disabled={i === 0}
                  style={miniBtn(i === 0)}>▲</button>
                <button type="button" onClick={() => move(r, +1)} disabled={i === list.length - 1}
                  style={miniBtn(i === list.length - 1)}>▼</button>
              </div>
              {r.related?.image_url
                ? <img src={r.related.image_url} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4 }} />
                : <div style={{ width: 36, height: 36, borderRadius: 4, background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>📦</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.related?.name || "—"}
                  {r.related && r.related.is_active === false && (
                    <span style={{ marginLeft: 6, fontSize: 9, padding: "1px 4px", borderRadius: 3, background: "var(--am)", color: "#fff" }}>
                      {lang === "es" ? "Inactivo" : "Inactive"}
                    </span>
                  )}
                </div>
                <input
                  value={r.note || ""}
                  onChange={(e) => setRelations((p) => p.map((x) => x.id === r.id ? { ...x, note: e.target.value } : x))}
                  onBlur={(e) => setNote(r, e.target.value)}
                  placeholder={lang === "es" ? "Nota opcional (ej. \"Combina con creatina\")" : "Optional note"}
                  style={{ ...inputCell, width: "100%", marginTop: 4 }} />
              </div>
              <button type="button" className="btn bs" style={{ fontSize: 11, color: "var(--am)" }}
                onClick={() => unlink(r)} title={lang === "es" ? "Desconectar" : "Unlink"}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const inputBox = {
  fontSize: 12, padding: "8px 10px",
  border: "1px solid var(--bd2)", borderRadius: 6,
  background: "var(--bg2)", color: "var(--tx)", fontFamily: "var(--f)",
};
const inputCell = {
  fontSize: 11, padding: "4px 6px",
  border: "1px solid var(--bd2)", borderRadius: 4,
  background: "var(--bg)", color: "var(--tx)", fontFamily: "var(--f)",
};
const miniBtn = (disabled) => ({
  fontSize: 9, padding: "1px 4px", lineHeight: 1,
  border: "1px solid var(--bd2)", borderRadius: 3,
  background: "var(--bg)", color: disabled ? "var(--tx3)" : "var(--tx2)",
  cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
});